/**
 * Anonymous pre-registration reads (KAN-1472 / KAN-1474).
 *
 * The platform landing's „try it" page walks the start-up wizard's first three
 * steps with no tenant behind it. These are the public siblings of
 * ``/admin/onboarding/*`` — deliberately NARROWER: the backend serves only what
 * this page draws (owner, 2026-09-18: „public endpointy servírují jen to
 * nejnutnější"), and a test there pins the exact key set.
 *
 * The wizard's step components speak ``WizardCatalog``; ``toWizardCatalog`` is
 * the one place the narrow payload is widened to that vocabulary. Every field
 * it fills with a blank is one the anonymous page never reads: own machines
 * (which need ``machine_config_schema``) and own operations are off there.
 */

import { useQueries, useQuery } from "@tanstack/react-query";

import type { MaterialOptionPublic } from "@/api/types";
import { apiFetch } from "@/lib/api";
import type {
  WizardCarrier,
  WizardCatalog,
  WizardVariantAxis,
} from "@/pages/onboarding/wizard/model";

const API = "/api/v1/public/onboarding";

interface PublicMachine {
  machine_key: string;
  kind: string;
  parent_key: string | null;
  name: string;
  vendor: string | null;
  build_volume_mm: number[] | null;
  material_codes: string[];
  /** KAN-1945 — offered by the landing-page sample (platform admin's pick). */
  in_demo?: boolean;
}

interface PublicOperation {
  slug: string;
  display_name: string;
  description: string | null;
  machines: PublicMachine[];
  addons: Array<{
    machine_key: string;
    name: string;
    description: string | null;
    applies_to_keys: string[];
  }>;
  materials: Array<{
    code: string;
    display_name: string;
    note: string | null;
    /** KAN-1559 — option values keyed by property key (a colour). */
    option_sources: Record<string, MaterialOptionPublic[]>;
    /** KAN-1559 — numeric axis values keyed by ``variant_axis.key``. */
    variant_axis_values: Record<string, number[]>;
  }>;
  /** The operation's numeric variant axis; its values come from the material. */
  variant_axis: WizardVariantAxis | null;
}

interface PublicOnboardingCatalog {
  operations: PublicOperation[];
  country_codes: string[];
}

interface PublicCarrier {
  code: string;
  name: string;
  delivery_method: string | null;
  country_codes: string[];
}

export interface PublicConfiguratorPreview {
  slug: string;
  /** Already in ``configurator_schema.fields`` vocabulary, reduced. */
  fields: Array<Record<string, unknown>>;
}

export function toWizardCatalog(payload: PublicOnboardingCatalog): WizardCatalog {
  return {
    operations: payload.operations.map((op) => ({
      slug: op.slug,
      display_name: op.display_name,
      description: op.description,
      operation_category: "",
      material_category: "",
      machine_config_schema: null,
      machines: op.machines.map((machine) => ({
        machine_key: machine.machine_key,
        kind: machine.kind === "variant" ? "variant" : "base",
        parent_key: machine.parent_key,
        name: machine.name,
        vendor: machine.vendor,
        build_volume_mm: machine.build_volume_mm,
        config: {},
        material_codes: machine.material_codes,
        in_demo: machine.in_demo ?? false,
      })),
      // What an addon changes is config — not served anonymously.
      addons: op.addons.map((addon) => ({ ...addon, changes: {} })),
      materials: op.materials.map((material) => ({ ...material, category_slug: "" })),
      variant_axis: op.variant_axis,
    })),
    countries: payload.country_codes.map((country_code) => ({ country_code, extra_days: 0 })),
  };
}

export function usePublicOnboardingCatalog(lang: string) {
  return useQuery<WizardCatalog>({
    queryKey: ["public-onboarding-catalog", lang],
    queryFn: async () =>
      toWizardCatalog(
        await apiFetch<PublicOnboardingCatalog>(
          `${API}/catalog?lang=${encodeURIComponent(lang)}`,
        ),
      ),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/** Processes the platform landing's switcher offers, in the admin's order
 *  (KAN-1962). Empty = the landing shows no configurator card. */
export const publicLandingDemoKey = ["public-landing-demo"] as const;

export function usePublicLandingDemo() {
  return useQuery<{ process_slugs: string[] }>({
    queryKey: publicLandingDemoKey,
    queryFn: () => apiFetch(`${API}/landing-demo`),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function usePublicOnboardingCarriers(countryCodes: string[], lang: string) {
  const countries = [...countryCodes].sort().join(",");
  return useQuery<WizardCarrier[]>({
    queryKey: ["public-onboarding-carriers", countries, lang],
    queryFn: async () =>
      (
        await apiFetch<PublicCarrier[]>(
          `${API}/carriers?countries=${encodeURIComponent(countries)}&lang=${encodeURIComponent(lang)}`,
        )
      ).map((carrier) => ({ ...carrier, extra_days: 0 })),
    enabled: countries !== "",
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function usePublicConfiguratorPreviews(slugs: string[]) {
  return useQueries({
    queries: slugs.map((slug) => ({
      queryKey: ["public-onboarding-configurator", slug],
      queryFn: () =>
        apiFetch<PublicConfiguratorPreview>(
          `${API}/operations/${encodeURIComponent(slug)}/configurator`,
        ),
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  });
}
